"use client";

import { ReactNode, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

type MarqueeProps = {
  /** Scroll speed in CSS pixels per second. */
  speed?: number;
  className?: string;
  stripClassName?: string;
  /**
   * The strip content. Must consist of two identical halves (an even number
   * of copies of the item set): the animation translates the strip by -50%
   * and jumps back, which is only seamless when both halves line up exactly.
   */
  children: ReactNode;
};

/**
 * Continuously scrolling strip driven by a CSS transform animation (the
 * shared `ticker` keyframes). This replaces embla's AutoScroll plugin, which
 * wrote a transform from a requestAnimationFrame loop and thereby kept the
 * main thread and compositor busy on every frame, even while offscreen.
 * The CSS animation runs on the compositor, is pausable via
 * prefers-reduced-motion, and is paused here whenever the strip leaves the
 * viewport, so it costs nothing on the rest of the page.
 */
export default function Marquee({
  speed = 30,
  className,
  stripClassName,
  children,
}: MarqueeProps) {
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    // One loop moves the strip by half its width; derive the duration from
    // that so the configured px/s speed holds regardless of logo count or
    // image sizes. The strip is w-max, so its own box grows as images load
    // and the ResizeObserver re-derives the duration (the early corrections
    // happen close to t=0, where rescaling the timeline is invisible).
    const updateDuration = () => {
      const half = strip.getBoundingClientRect().width / 2;
      if (half > 0) {
        strip.style.animationDuration = `${half / speed}s`;
      }
    };
    updateDuration();
    const resizeObserver = new ResizeObserver(updateDuration);
    resizeObserver.observe(strip);

    // Play-state is controlled exclusively through the data attributes below
    // and their stylesheet rules; setting it inline would override the
    // prefers-reduced-motion rule that holds tickers still.
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      strip.toggleAttribute("data-offscreen", !entry.isIntersecting);
    });
    intersectionObserver.observe(strip);

    // Hold the strip at translateX(0) until its images have settled: a width
    // change mid-animation re-resolves the -50% loop translation and visibly
    // jumps the strip. At progress 0 a width change moves nothing.
    // Lazy images must not gate the start: ones beyond the viewport edge of a
    // paused strip never begin loading, which would deadlock the marquee.
    const abort = new AbortController();
    let failSafe: number | undefined;
    const pendingImages = Array.from(strip.querySelectorAll("img")).filter(
      (img) => img.loading !== "lazy" && !img.complete
    );
    if (pendingImages.length > 0) {
      strip.toggleAttribute("data-loading", true);
      const start = () => strip.removeAttribute("data-loading");
      let settled = 0;
      const onSettled = () => {
        settled += 1;
        if (settled === pendingImages.length) {
          start();
        }
      };
      for (const img of pendingImages) {
        img.addEventListener("load", onSettled, { once: true, signal: abort.signal });
        img.addEventListener("error", onSettled, { once: true, signal: abort.signal });
      }
      // Fail-safe: a stalled request must never freeze the marquee; starting
      // anyway just risks a small, bounded jump if that image lands later.
      failSafe = window.setTimeout(start, 4000);
    }

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      abort.abort();
      window.clearTimeout(failSafe);
      // Clear the paused-state attributes: if the effect re-runs after the
      // images have completed, the gate above is skipped and a stale
      // data-loading would otherwise pause the strip forever.
      strip.removeAttribute("data-loading");
      strip.removeAttribute("data-offscreen");
    };
  }, [speed]);

  return (
    <div className={cn("overflow-hidden", className)}>
      <div
        ref={stripRef}
        className={cn("flex w-max animate-ticker", stripClassName)}
        // Slow placeholder pace until the real duration is measured.
        style={{ animationDuration: "120s" }}
      >
        {children}
      </div>
    </div>
  );
}
